import React, { ReactNode, ComponentType } from 'react';
import { cn } from 'src/utils/Functions';

interface ChitBadgeProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'default';
  icon?: ReactNode | ComponentType<any>;
  className?: string;
}

export const ChitBadge: React.FC<ChitBadgeProps> = ({
  children,
  variant = 'default',
  size = 'default',
  icon,
  className
}) => {
  const baseClasses = "inline-flex items-center gap-1 rounded-full font-medium";
  
  const sizeClasses = {
    sm: "px-2 py-0.5 text-xs",
    default: "px-2.5 py-1 text-sm"
  };

  const variantClasses = {
    default: "bg-neutral-700/50 text-white/60 border border-white/20",
    success: "bg-green-500/20 text-green-400 border border-green-500/30",
    warning: "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30", 
    error: "bg-red-500/20 text-red-400 border border-red-500/30",
    info: "bg-blue-500/20 text-blue-400 border border-blue-500/30"
  };

  return (
    <span className={cn(
      baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      className
    )}>
      {icon && (
        <span className="w-3 h-3 flex items-center justify-center">
          {React.isValidElement(icon) ? icon : React.createElement(icon as ComponentType<{ className?: string }>, { className: "w-3 h-3" })}
        </span>
      )}
      {children}
    </span>
  );
};

// Specialized badge for inline status/role indicators in text
interface ChitStatusProps {
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  icon?: ReactNode | ComponentType<any>;
  className?: string;
}

export const ChitStatus: React.FC<ChitStatusProps> = ({
  children,
  variant = 'default',
  icon,
  className
}) => {
  const variantClasses = {
    default: "text-white/40",
    success: "text-green-400",
    warning: "text-yellow-400", 
    error: "text-red-400",
    info: "text-blue-400"
  };

  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-sm",
      variantClasses[variant],
      className
    )}>
      {icon && (
        <span className="w-4 h-4 flex items-center justify-center">
          {React.isValidElement(icon) ? icon : React.createElement(icon as ComponentType<{ className?: string }>, { className: "w-4 h-4" })}
        </span>
      )}
      {children}
    </span>
  );
};